import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Fragment } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  AveragePlayerScore,
  So5FixtureEvent,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { VerticalDivider } from '@sorare/core/src/atoms/ui/Divider';
import { Gain } from '@sorare/core/src/components/scoring/Gain';
import { PlayerAppearance } from '@sorare/core/src/components/scoring/PlayerAppearance';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';
import { ProjectionScore } from '@sorare/core/src/components/scoring/ProjectionScore';
import { useDefaultAverageScore } from '@sorare/core/src/hooks/useDefaultAverageScore';
import type { TotalAveragePlayerScore } from '@sorare/core/src/lib/averagePlayerScore';
import { isTotalAveragePlayerScore } from '@sorare/core/src/lib/averagePlayerScore';
import { cardAttributes, scoutingCenter } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';

import type { AveragePlayerScores_anyPlayer } from './__generated__/index.graphql.ts';

const Root = styled(SBHorizontal).attrs({ gap: 0 })`
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  padding: var(--double-unit);
  @media ${desktopAndAbove} {
    padding: var(--triple-unit);
  }
`;

const Item = styled(Vertical)`
  width: 100%;
  flex: 0 1 100%;
  @media ${desktopAndAbove} {
    flex-direction: row;
  }
`;

const SyledVerticalDivider = styled(VerticalDivider)`
  height: var(--quadruple-unit);
`;

type Mode =
  | TotalAveragePlayerScore
  | `projection-${So5FixtureEvent}`
  | `difference-${So5FixtureEvent}`;

const useAveragesPlayerScoresModes = (): Record<Sport, Mode[]> => {
  const defaultAverageScore = useDefaultAverageScore();
  return {
    [Sport.FOOTBALL]: [
      AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE,
      defaultAverageScore,
      AveragePlayerScore.LAST_FORTY_SO5_AVERAGE_SCORE,
    ],
    [Sport.NBA]: [
      AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE,
      `projection-${So5FixtureEvent.CLASSIC}` as Mode,
      `difference-${So5FixtureEvent.CLASSIC}` as Mode,
    ],
    [Sport.BASEBALL]: [
      defaultAverageScore,
      AveragePlayerScore.SEASON_AVERAGE_SCORE,
    ],
  };
};

const averageScoreMessages: { [K in Mode]?: MessageDescriptor } = {
  [AveragePlayerScore.LAST_FORTY_SO5_AVERAGE_SCORE]: cardAttributes.last40,
  [AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE]: cardAttributes.last15,
  [AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE]: cardAttributes.last5,
  [AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE]: cardAttributes.last10,
  [AveragePlayerScore.SEASON_AVERAGE_SCORE]: cardAttributes.season,
  [`projection-${So5FixtureEvent.DAILY}`]: scoutingCenter.projectionDaily,
  [`projection-${So5FixtureEvent.CLASSIC}`]: scoutingCenter.projectionClassic,
  [`difference-${So5FixtureEvent.CLASSIC}`]:
    scoutingCenter.projectionDifferenceClassic,
  [`difference-${So5FixtureEvent.DAILY}`]:
    scoutingCenter.projectionDifferenceDaily,
};

const getModeAndValue = (
  player: AveragePlayerScores_anyPlayer,
  mode: Mode
): {
  value: number | null;
  projectionMode?: So5FixtureEvent;
  differenceMode?: So5FixtureEvent;
} => {
  if (isType(player, 'Player')) return { value: null };
  switch (mode) {
    case `projection-${So5FixtureEvent.DAILY}`:
      return {
        value: player.nextDailyFixtureProjectedScore,
        projectionMode: So5FixtureEvent.DAILY,
      };
    case `projection-${So5FixtureEvent.CLASSIC}`:
      return {
        value: player.nextClassicFixtureProjectedScore,
        projectionMode: So5FixtureEvent.CLASSIC,
      };
    case `difference-${So5FixtureEvent.DAILY}`:
      return {
        value: player.gainOnDailyProjection,
        differenceMode: So5FixtureEvent.DAILY,
      };
    case `difference-${So5FixtureEvent.CLASSIC}`:
      return {
        value: player.gainOnClassicProjection,
        differenceMode: So5FixtureEvent.CLASSIC,
      };
    default:
      return { value: null };
  }
};

export const AveragePlayerScores = ({
  player,
  modes: modesFromProps,
}: {
  player: Nullable<AveragePlayerScores_anyPlayer>;
  modes?: Mode[];
}) => {
  const averagesPlayerScoresModes = useAveragesPlayerScoresModes();
  if (!player) return null;
  const modes = modesFromProps || averagesPlayerScoresModes[player.sport];

  const filteredModes = modes.filter(mode => {
    const isAverageScore = isTotalAveragePlayerScore(mode);
    const { projectionMode, differenceMode } = getModeAndValue(player, mode);
    return isAverageScore || !!projectionMode || !!differenceMode;
  });

  return (
    <Root>
      {filteredModes.map((mode, index) => {
        const isAverageScore = isTotalAveragePlayerScore(mode);
        const { value, projectionMode, differenceMode } = getModeAndValue(
          player,
          mode
        );

        return (
          <Fragment key={mode}>
            <Item center className="text-center">
              <LabelM bold color="var(--c-nd-600)">
                <FormattedMessage {...averageScoreMessages[mode]} />
              </LabelM>
              {!!isAverageScore && (
                <>
                  <PlayerScore player={player} mode={mode} animated={false} />
                  {mode !== AveragePlayerScore.SEASON_AVERAGE_SCORE && (
                    <PlayerAppearance
                      size="small"
                      withTooltip
                      player={player}
                      mode={mode}
                    />
                  )}
                </>
              )}
              {!!projectionMode && (
                <ProjectionScore
                  size={4}
                  withLogo
                  withTooltip
                  projectedScore={value}
                  so5FixtureEvent={projectionMode}
                />
              )}
              {!!differenceMode && <Gain value={value} size={4} />}
            </Item>
            {index !== filteredModes.length - 1 && <SyledVerticalDivider />}
          </Fragment>
        );
      })}
    </Root>
  );
};

AveragePlayerScores.fragments = {
  anyPlayer: gql`
    fragment AveragePlayerScores_anyPlayer on AnyPlayerInterface {
      slug
      sport
      lastFortySo5AverageScore: averageScore(type: LAST_FORTY_SO5_AVERAGE_SCORE)
      lastFifteenSo5AverageScore: averageScore(
        type: LAST_FIFTEEN_SO5_AVERAGE_SCORE
      )
      lastFiveSo5AverageScore: averageScore(type: LAST_FIVE_SO5_AVERAGE_SCORE)
      lastTenPlayedSo5AverageScore: averageScore(
        type: LAST_TEN_PLAYED_SO5_AVERAGE_SCORE
      )
      seasonAverage: averageScore(type: SEASON_AVERAGE_SCORE)
      nextClassicFixtureProjectedScore
      nextDailyFixtureProjectedScore
      gainOnClassicProjection
      gainOnDailyProjection
      ...PlayerAppearance_anyPlayer
    }
    ${PlayerAppearance.fragments.anyPlayer}
  ` as TypedDocumentNode<AveragePlayerScores_anyPlayer>,
};
